import { motion } from "framer-motion";
import { Camera, Video, BatteryCharging, Truck, PackageCheck, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { scrollToId } from "@/components/Navbar";

const CARDS = [
  {
    icon: Camera,
    num: "01",
    title: "Front Dash Cam Installation",
    desc: "Single-channel precision fitment with cables fully hidden behind the headliner and A-pillar trim.",
    img: "https://images.unsplash.com/photo-1785663291408-4aca8656cb24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxjYXIlMjBkYXNoJTIwY2FtJTIwaW5zdGFsbGF0aW9uJTIwaW50ZXJpb3IlMjB3aW5kc2hpZWxkJTIwdmlld3xlbnwwfHx8fDE3OTIxNDc1NDF8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-4",
    tall: true,
  },
  {
    icon: Video,
    num: "02",
    title: "Dual-Channel Front + Rear",
    desc: "Full 4K front and rear coverage, hardwired and synced in one clean job.",
    img: "https://images.pexels.com/photos/15253512/pexels-photo-15253512.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-2",
  },
  {
    icon: BatteryCharging,
    num: "03",
    title: "24/7 Parking Mode",
    desc: "Smart fuse tapping with voltage cut-off — records while parked, never drains your battery.",
    img: "https://images.unsplash.com/photo-1632733711679-529326f6db12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxhdXRvJTIwZWxlY3RyaWNpYW4lMjB3aXJpbmclMjBtZWNoYW5pYyUyMGNhciUyMGluc3RhbGxhdGlvbiUyMGRldGFpbHxlbnwwfHx8fDE3OTIxNDc1NDF8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-2",
  },
  {
    icon: Truck,
    num: "04",
    title: "Fleet & Commercial",
    desc: "Multi-vehicle rollouts for tradies, couriers and fleets — invoiced, scheduled, uniform.",
    img: "https://images.unsplash.com/photo-1628577478162-d4d00467c627?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxhdXRvJTIwZWxlY3RyaWNpYW4lMjB3aXJpbmclMjBtZWNoYW5pYyUyMGNhciUyMGluc3RhbGxhdGlvbiUyMGRldGFpbHxlbnwwfHx8fDE3OTIxNDc1NDF8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-2",
  },
  {
    icon: PackageCheck,
    num: "05",
    title: "Supply + Install Bundles",
    desc: "We supply top-tier BlackVue, VIOFO, Thinkware and ClearView units, fitted the same day.",
    img: "https://images.unsplash.com/photo-1645400379459-f6fd3d963fd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBhdXRvbW90aXZlJTIwc3BvcnRzJTIwY2FyJTIwY29ja3BpdCUyMG5pZ2h0JTIwaGVhZGxpZ2h0cyUyMGhlcm98ZW58MHx8fHwxNzkyMTQ3NTQyfDA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-2",
  },
];

const Card = ({ card, index }) => (
  <motion.button
    data-testid={`service-card-${index + 1}`}
    onClick={() => scrollToId("contact")}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden rounded-xl border border-white/10 bg-carbon text-left hover:border-sky-500/40 transition-[border-color,box-shadow] duration-500 hover:shadow-[0_0_35px_rgba(14,165,233,0.12)] ${card.span} ${card.tall ? "min-h-[26rem]" : "min-h-[18rem]"}`}
  >
    <img
      src={card.img}
      alt={card.title}
      className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-[opacity,transform] duration-700"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/10" />
    <div className="relative h-full flex flex-col justify-between p-6 sm:p-7">
      <div className="flex items-start justify-between">
        <span className="font-mono text-xs text-sky-500/80 tracking-[0.3em]">{card.num}</span>
        <span className="p-2 rounded-md bg-obsidian/60 border border-white/10 text-sky-500 group-hover:bg-sky-500 group-hover:text-obsidian transition-[background-color,color] duration-300">
          <card.icon size={18} />
        </span>
      </div>
      <div>
        <h3 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl text-gray-50 flex items-center gap-2">
          {card.title}
          <ArrowUpRight size={20} className="text-sky-500 opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-[opacity,transform] duration-300" />
        </h3>
        <p className="mt-2 text-sm text-gray-400 leading-relaxed max-w-md">{card.desc}</p>
      </div>
    </div>
  </motion.button>
);

const Services = () => (
  <section id="services" data-testid="services-bento-grid" className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
    <span className="pointer-events-none select-none absolute -top-6 right-0 font-display font-black uppercase text-[18vw] leading-none text-outline opacity-40 hidden lg:block">
      Autoric
    </span>
    <SectionHeading eyebrow="What we do" title="Installations, engineered" testId="services-heading" />
    <Reveal>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
        {CARDS.map((c, i) => (
          <Card key={c.num} card={c} index={i} />
        ))}
      </div>
    </Reveal>
  </section>
);

export default Services;
